/**
 * SessionManager — manages session lifecycle via localStorage and API
 */

import { ENDPOINTS } from './endpoints.js';
import { ApiError } from './ApiClient.js';

const STORAGE_KEY = '_garderoboSmartSearchSessionKey';

export default class SessionManager {
  /**
   * @param {import('./ApiClient.js').default} apiClient
   */
  constructor(apiClient) {
    this._apiClient = apiClient;
    this._sessionKey = null;
  }

  /**
   * Full session initialization cycle:
   * 1. Read session_key from localStorage
   * 2. If none — POST /start-session {} → save to localStorage
   * 3. If userId provided — POST /start-session {session_key, user_id} to bind
   * 4. Handle 403 (invalid session) — clear and recreate
   *
   * @param {string|null} userId - External user ID
   */
  async initSession(userId) {
    const storedKey = this._readFromStorage();

    if (storedKey) {
      // Try to use existing session
      try {
        const result = await this._startSession(storedKey, userId);
        this._setSessionKey(result.data.session_key);
        return;
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          // Invalid session — clear and create new
          console.warn('[GarderoboSearch] Stored session invalid, creating new one');
          this._clearStorage();
        } else {
          throw err;
        }
      }
    }

    // Create new session
    const result = await this._startSession(null, userId);
    this._setSessionKey(result.data.session_key);
  }

  /**
   * Get current session key
   * @returns {string|null}
   */
  getSessionKey() {
    return this._sessionKey;
  }

  /**
   * Clear session from memory and localStorage
   */
  clearSession() {
    this._sessionKey = null;
    this._apiClient.setSessionKey(null);
    this._clearStorage();
  }

  /**
   * @private
   * Call start-session API
   * @param {string|null} sessionKey
   * @param {string|null} userId
   * @returns {Promise<Object>}
   */
  async _startSession(sessionKey, userId) {
    const body = {};
    if (sessionKey) body.session_key = sessionKey;
    if (userId) body.user_id = userId;
    return this._apiClient.post(ENDPOINTS.START_SESSION, body);
  }

  /**
   * @private
   * Set session key in memory, ApiClient, and localStorage
   */
  _setSessionKey(key) {
    this._sessionKey = key;
    this._apiClient.setSessionKey(key);
    this._writeToStorage(key);
  }

  /** @private */
  _readFromStorage() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** @private */
  _writeToStorage(key) {
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      // localStorage unavailable — session works only in memory
    }
  }

  /** @private */
  _clearStorage() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}
